import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AccessList,
  useDateFns,
  useHasAccess,
  useIntegrations,
  useProvider,
  useRequest,
  useCreatedPartnerPortalChecklist,
} from '~/Hooks';
import { EventType } from '~/Models/DTO/Event.model';
import { ItemCreationFieldValue, ItemCreationSection } from '~/Components';
import { GeneralSection } from '../EventCreateSections/GeneralSection';
import { EventDetailsSection } from '../EventCreateSections/EventDetailsSection/EventDetailsSection';
import { EventScheduleSection } from '../EventCreateSections/EventScheduleSection/EventScheduleSection';
import { SettingsSection } from '../EventCreateSections/SettingsSection';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { getEventGroupById } from '../APIs';
import { GRAY_DARK_SHADE_1, WHITE } from '~/Constants/Colors.constants';
import {
  useCreateNewEventGroupRequest,
  useEventCreateHeaders,
  useUpdateEventGroupRequest,
  useZoomIntegration,
} from '~/Screens/Events/EventCreate/hooks';
import { Segment } from '~/Helpers/Segment.helper';
import { SegmentName } from '~/Constants/Segment.constants';
import { Theme, useMediaQuery } from '@mui/material';
import { isNil } from 'lodash-es';
import { ExtendedEventSeries } from '../EventCreateSections/EventScheduleSection/extendedEventSeries.model';
import { ExternalPartyID } from '~/Models/DTO/externalParties.model';
import { useEventCreateValidation } from './useEventCreateValidation';
import { useEventCreateInitializer } from './useEventCreateInitializer';

const getSections = (
  setFieldValue: ItemCreationFieldValue,
  eventType: EventType,
  liveStreamEnabled: boolean,
): ItemCreationSection<ExtendedEventGroup>[] => [
  ({ data }) => (
    <GeneralSection eventGroup={data} eventType={eventType} liveStreamEnabled={liveStreamEnabled} />
  ),
  ({ data }) => <EventDetailsSection eventGroup={data} />,
  ({ data }) => (
    <EventScheduleSection setFieldValue={setFieldValue} eventGroup={data} eventType={eventType} />
  ),
  ({ data }) => <SettingsSection eventGroup={data} />,
];
export const useEventCreate = () => {
  //hooks
  const { currentProvider, isUsingThePartnerTool } = useProvider();

  const {
    createdPartnerChecklist,
    updateCreatedPartnerChecklist,
  } = useCreatedPartnerPortalChecklist();

  const {
    id,
    eventGroup,
    setEventGroup,
    eventType,
    locationList,
    trainerList,
  } = useEventCreateInitializer();
  const [liveStreamEnabled, setLiveStreamEnabled] = useState(false);

  const { request, requestWithCallback } = useRequest();
  const { hasIntegration } = useIntegrations();
  const isUSCIntegrationEnabled = useMemo(() => hasIntegration(ExternalPartyID.USC_INTEGRATION), [
    hasIntegration,
  ]);
  const isEditEventGroup = useMemo(() => !isNil(id), [id]);
  const { formatDate, parse } = useDateFns();
  const { createNewEventGroup } = useCreateNewEventGroupRequest();
  const { updateEventGroup } = useUpdateEventGroupRequest();
  useZoomIntegration({ setEventGroup });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const { hasAccess } = useHasAccess();
  const {
    isGeneralSectionDone,
    isDetailsSectionDone,
    isScheduleSectionDone,
    isSettingsSectionDone,
    setIsScheduleSectionDone,
    disableSubmit,
    validate: validateExtend,
  } = useEventCreateValidation({
    eventType,
    eventGroup,
    setEventGroup,
    trainerList,
    locationList,
  });

  const allowLiveStream = locationList?.[0]?.liveStreamEnabled || !isUsingThePartnerTool;
  useEffect(() => {
    setLiveStreamEnabled(allowLiveStream);
  }, [locationList?.length]);

  const validate = useCallback(
    (data: any) => {
      if (data.locationId) {
        const currentLocation = locationList.find(({ uuid }: any) => uuid === data?.locationId);
        if (currentLocation?.liveStreamEnabled !== liveStreamEnabled) {
          setLiveStreamEnabled(
            currentLocation?.liveStreamEnabled || !isUsingThePartnerTool || false,
          );
        }
      }
      return validateExtend(data);
    },
    [validateExtend, locationList?.length],
  );
  //state
  const [isLoading, setIsLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<IErrorResponse>({});
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const isForcedEventGroupUSCSync = hasAccess(AccessList.FORCE_EVENT_GROUP_USC_SYNC);
  const setFieldValue = (field: string, value: string | ExtendedEventSeries[]) => {
    if (field === 'timeSeries' && typeof value !== 'string') {
      const scheduleSectionDone = !!value?.some((t: ExtendedEventSeries) => !t.shouldDelete);
      setIsScheduleSectionDone(scheduleSectionDone);
    }

    setEventGroup((prevEventGroup) => ({
      ...prevEventGroup,
      [field]: value,
    }));
  };

  const headers = useEventCreateHeaders({
    eventType,
    isGeneralSectionDone,
    isDetailsSectionDone,
    isScheduleSectionDone,
    isSettingsSectionDone,
  });
  const sections: ItemCreationSection<ExtendedEventGroup>[] = useMemo(
    () => getSections(setFieldValue, eventType, liveStreamEnabled),
    [eventType, liveStreamEnabled],
  );

  const onSave = async () => {
    if (disableSubmit) {
      setShowFormErrors(true);
      return;
    }

    setIsLoading(true);

    const eventGroupToSend = {
      ...eventGroup,
      timeSeries: eventGroup.timeSeries?.map((timeSeriesItem) => ({
        ...timeSeriesItem,
        dailyStartTime: formatDate(timeSeriesItem.dailyStartTime, 'HH:mm:ss'),
        dailyEndTime: formatDate(timeSeriesItem.dailyEndTime, 'HH:mm:ss'),
        startDate: `${formatDate(timeSeriesItem.startDate, 'yyyy-MM-dd')}T00:00:00`,
        endDate: timeSeriesItem.hasEndDate
          ? `${formatDate(timeSeriesItem.endDate, 'yyyy-MM-dd')}T00:00:00`
          : undefined,
      })),
    };
    try {
      if (currentProvider) {
        const { email, domain } = currentProvider;
        Segment.track(
          eventGroupToSend.id ? SegmentName.EDIT_EVENT_GROUP_SAVE : SegmentName.CREATE_EVENT_SAVE,
          {
            email,
            domain,
            eventType,
            isUsingPartnerPortal: isUsingThePartnerTool,
          },
        );
      }
      if (!eventGroupToSend.id) {
        await createNewEventGroup({
          setSubmissionError,
          eventGroupToSend: eventGroupToSend as ExtendedEventGroup,
        });
      } else {
        await updateEventGroup({
          setSubmissionError,
          eventGroupToSend: { ...eventGroupToSend, type: eventType } as ExtendedEventGroup,
        });
      }
      if (!createdPartnerChecklist?.eventCreated) {
        updateCreatedPartnerChecklist({ eventCreated: new Date().toISOString() });
        Segment.track(SegmentName.CREATED_PARTNER_PORTAL_CHECKLIST_EVENT_CREATED);
      }
    } catch (err) {
      console.error('failed to save the eventGroup', err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (id) {
      getEventGroupById(
        request,
        requestWithCallback,
        id,
        isUSCIntegrationEnabled,
        isForcedEventGroupUSCSync,
        validate,
        setSubmissionError,
        formatDate,
        parse,
        eventType,
      );
    }
  }, [id, eventType]);

  return {
    error: Object.keys(submissionError ?? {}).length ? submissionError : undefined,
    onSave,
    headers,
    validate,
    isLoading,
    listItems: sections,
    eventType,
    eventGroup,
    disableSubmit,
    showFormErrors,
    backgroundColor: mobile ? GRAY_DARK_SHADE_1 : WHITE,
    isEditEventGroup,
  };
};
